/**
 * AP Invoices Demo — reactive accessors for demo-only narrative slices that
 * real components surface (match provenance, 3-way match).
 *
 * THROWAWAY DEMO CODE. Each returns `undefined` when the demo is off, so the
 * host component renders nothing in production.
 */

import { useApDemoFixture } from './hook-shim';
import type {
  MatchProvenance,
  ThreeWayMatch,
  ExtractedDocument,
  ApLogisticsLink,
  ApRemittance,
} from './fixture-store.types';

/** Memory-bank provenance for the supplier match on an invoice. */
export function useApSupplierProvenance(
  invoiceId: string,
): MatchProvenance | undefined {
  return useApDemoFixture((s) => s.supplierProvenanceByInvoiceId[invoiceId]);
}

/** Web-search provenance for a matched invoice line item. */
export function useApLineItemProvenance(
  lineItemId: string,
): MatchProvenance | undefined {
  return useApDemoFixture((s) => s.lineItemProvenanceById[lineItemId]);
}

/** 3-way match (invoice ↔ PO ↔ POD) data for an invoice. */
export function useApThreeWayMatch(
  invoiceId: string,
): ThreeWayMatch | undefined {
  return useApDemoFixture((s) => s.threeWayByInvoiceId[invoiceId]);
}

/**
 * Extracted-document data (structured fields + line items) for a matched
 * CORE_DOCUMENT — backs the per-line "Document" column popout.
 */
export function useApExtractedDocument(
  documentId: string | null | undefined,
): ExtractedDocument | undefined {
  return useApDemoFixture((s) =>
    documentId ? s.extractedDocsByDocumentId[documentId] : undefined,
  );
}

/** Whether the early-payment discount has been approved for an invoice. */
export function useApEarlyPayApproved(invoiceId: string): boolean {
  return (
    useApDemoFixture(
      (s) => s.earlyPayApprovedByInvoiceId[invoiceId] ?? false,
    ) ?? false
  );
}

/** Whether a web-search line match has been saved to the memory bank. */
export function useApLineMemorySaved(lineItemId: string): boolean {
  return (
    useApDemoFixture((s) => s.lineMemorySavedById[lineItemId] ?? false) ?? false
  );
}

/** Product↔freight cross-link card data for an invoice. */
export function useApLogisticsLink(
  invoiceId: string,
): ApLogisticsLink | undefined {
  return useApDemoFixture((s) => s.logisticsLinkByInvoiceId[invoiceId]);
}

/** The remittance email state (null when closed / demo off). */
export function useApRemittance(): ApRemittance | null {
  return useApDemoFixture((s) => s.remittance) ?? null;
}

/** Supplier invoice-history dialog state (undefined when demo off). */
export function useApSupplierHistory(): { open: boolean } | undefined {
  return useApDemoFixture((s) => s.supplierHistory);
}

/** AP processing-workflow build state (undefined when demo off). */
export function useApWorkflowBuild():
  | { built: boolean; confidenceAdded: boolean }
  | undefined {
  return useApDemoFixture((s) => s.apWorkflow);
}
